// Canonical keys for matching Thai loanword spellings to their likely
// English source words. Two entry points share one collapse:
//
//   englishLoanwordKey('time')            → 'taim'
//   thaiGuessKeys('thaim')  (from ไทม์)   → ['taim']
//
// The English side runs over the eng_words dictionary at index-build time
// (scripts/build-loanword-index.ts → src/data/loanword-index.json); the
// Thai side runs in the client over romanizeSyllableForGuess output. Any
// English word and Thai transcription pair that collapse to the same key
// match with a single map lookup.
//
// The philosophy is the opposite of the worker's translit-key.ts: there a
// miss is worse than a collision because results are a ranked list. Here
// the output is a single user-visible "maybe:" guess, so precision beats
// recall — a miss just leaves the entry blank like today. Vowels are kept
// as coarse classes rather than dropped, and only the Thai side branches
// on genuinely ambiguous spellings.
//
// Kept dependency-free so the index build script can import it directly.

// ง / English "ng" is a single sound; shield it from the g and n rules
const shieldNg = (s: string) => s.replace(/ng/g, 'ŋ')

// Reductions valid for both a romanized Thai spelling and a normalized
// English spelling. Mostly RTGS vowel clusters onto the coarse vowel the
// English side produces.
const collapseShared = (s: string): string => {
   // Post-vocalic h marks vowel quality, not a sound (โอห์ม "ohm" ≡ "ohm")
   s = s.replace(/([aeiou])h/g, '$1')

   s = s
      .replace(/aeo/g, 'ao') // แอว before the ae rule below
      .replace(/ae/g, 'a') // แอ ≡ English short a
      .replace(/ia/g, 'i') // เอีย ≡ ea/ee
      .replace(/i[wu]/g, 'u') // อิว ≡ ew ("new", "view")
      .replace(/ew/g, 'u')
      .replace(/iu/g, 'u') // the ew rule can surface a fresh iu ("wiew" → wiu)
      .replace(/uea/g, 'ua') // เอือ
      .replace(/[ue]e/g, 'u') // อือ
      .replace(/eu/g, 'u')
      .replace(/ei/g, 'e')
      .replace(/ow/g, 'ao') // โชว์ "chow" ≡ "show" → chao
      .replace(/rl/g, 'l') // เวิลด์ drops the r of "world"

   // Doubled letters carry no signal by this point
   s = s.replace(/(.)\1+/g, '$1')

   return s.replace(/ŋ/g, 'ng')
}

/**
 * Collapses an English dictionary spelling to its loanword-matching key.
 * Returns '' when the input can't be keyed (non-letters, too short).
 */
export function englishLoanwordKey(word: string): string {
   let s = word.toLowerCase()
   if (!/^[a-z]{2,32}$/.test(s)) return ''

   // Unlike the Thai side, English "ng" before e isn't the ง sound — the g
   // is soft ("change" says chenj) and must stay for the g rule below
   s = s.replace(/ng(?!e)/g, 'ŋ')

   // Doubled consonants are single sounds (app → ap); vowel pairs like
   // ee/oo are real digraphs and handled below
   s = s.replace(/([^aeiou])\1+/g, '$1')

   // ai/ay say the เอ sound ("mail" เมล). Must run before igh and magic-e
   // below, whose produced "ai" (the ไอ sound) has to survive.
   s = s.replace(/a[iy]/g, 'e')
   s = s.replace(/igh/g, 'ai')

   // Spelling-level digraphs
   s = s
      .replace(/qu/g, 'kw')
      .replace(/x/g, 'ks')
      .replace(/wh/g, 'w')
      .replace(/ck/g, 'k')
      .replace(/ph/g, 'f')
      .replace(/sh/g, 'ch')
      .replace(/th/g, 't')

   // Soft/hard c and g by the following letter; the negative lookahead
   // keeps the ch digraph (original or from sh) intact
   s = s.replace(/c(?=[eiy])/g, 's').replace(/c(?!h)/g, 'k')
   s = s.replace(/g(?=[eiy])/g, 'j').replace(/g(?!h)/g, 'k')
   s = s.replace(/j/g, 'ch').replace(/z/g, 's').replace(/v/g, 'w')

   // Syllabic -le ("google" is transcribed เกิล, i.e. "el")
   s = s.replace(/([^aeiou])le$/, '$1el')

   // Non-initial y is a vowel
   s = s.replace(/(?!^)y/g, 'i')

   // r-controlled ir/ur sound like er (bird เบิร์ด → berd), and "or"
   // after w does too ("work" เวิร์ก). Must run before the vowel digraphs
   // so "beer"/"cheer" keep their ee (≡ เอีย) instead of merging into
   // "chair"-style er.
   s = s.replace(/wor/g, 'wer')
   s = s.replace(/[iu]r/g, 'er')

   // Vowel digraphs onto their Thai-transcription vowels. "our" says
   // "or" ("course" คอร์ส) and must beat the generic ou→ao; English "oe"
   // says "o" ("goes"), unlike the RTGS "oe" the Thai side collapses.
   s = s
      .replace(/e[ae]/g, 'i') // ee/ea ≡ อี
      .replace(/oo/g, 'u')
      .replace(/oa/g, 'o')
      .replace(/oe/g, 'o')
      .replace(/our/g, 'or')
      .replace(/ou/g, 'ao')
      .replace(/aw|au/g, 'o')

   // Magic e: a silent final e changes the preceding vowel's sound, also
   // reaching across a nasal or a ch digraph ("change" → chench, "page" →
   // pech — the ch may itself come from the soft-g rule above)
   s = s.replace(
      /([aeiou])(n?)(ch|[^aeiou])e$/,
      (_, vowel: string, nasal: string, consonant: string) => {
         const changed: Record<string, string> = { a: 'e', e: 'i', i: 'ai', o: 'o', u: 'u' }
         // "are" says อา not เอ ("software" ซอฟต์แวร์)
         const newVowel = vowel === 'a' && !nasal && consonant === 'r' ? 'a' : changed[vowel]
         return `${newVowel}${nasal}${consonant}`
      }
   )
   // A final e after a consonant cluster is still silent even though the
   // magic-e pattern can't match it ("serve" → serw). Only after a real
   // cluster — a lone consonant + e can be a spoken vowel ("day" → de).
   s = s.replace(/([^aeiou]{2})e$/, '$1')

   s = collapseShared(s)
   return /^[a-z]{2,32}$/.test(s) ? s : ''
}

// Genuinely ambiguous Thai spellings branch into both readings, identity
// first: อั/อะ/อา all romanize to "a" but often transcribe an English
// short u ("update" อัปเดต), and ฟ spells both English f and v ("drive"
// ไดรฟ์). Branching bounds: 2^4 variants.
const THAI_BRANCHES: Record<string, string> = { a: 'u', f: 'w' }
const MAX_BRANCH_POINTS = 4

/**
 * Collapses a romanized-for-guessing Thai spelling (romanizeSyllableForGuess
 * output, possibly several syllables concatenated) to its candidate keys.
 * The unbranched spelling comes first so it wins when several keys match.
 * Returns [] when the input can't be keyed.
 */
export function thaiGuessKeys(romanized: string): string[] {
   let s = romanized.toLowerCase()
   if (!/^[a-z]{2,32}$/.test(s)) return []

   s = shieldNg(s)

   // RTGS aspiration digraphs are single Thai letters (ท th, ข kh, พ ph),
   // and ฟล romanizes to "fhl"
   s = s.replace(/th/g, 't').replace(/kh/g, 'k').replace(/ph/g, 'p').replace(/fh/g, 'f')

   // RTGS "oe"/"oei" (เอิ เออ เอย) say the English er/ay sounds — these are
   // Thai-side spellings, unlike the English "oe" of "goes"
   s = s.replace(/oei/g, 'e').replace(/oe/g, 'e')

   // Non-initial y is a vowel glide, same as the English side (เดย์ "dey")
   s = s.replace(/(?!^)y/g, 'i')

   s = collapseShared(s)
   if (!/^[a-z]{2,32}$/.test(s)) return []

   // Branch each of the first few ambiguous letters, identity first
   const branchPositions: number[] = []
   for (let i = 0; i < s.length && branchPositions.length < MAX_BRANCH_POINTS; i++) {
      if (THAI_BRANCHES[s[i]]) branchPositions.push(i)
   }

   const keys = [s]
   for (const position of branchPositions) {
      const replacement = THAI_BRANCHES[s[position]]
      for (const key of [...keys]) {
         keys.push(`${key.slice(0, position)}${replacement}${key.slice(position + 1)}`)
      }
   }
   // A branched letter can enable further reductions (ไดรฟ์ "draif" →
   // "draiw" → "drau"), so collapse each variant again
   return [...new Set(keys.map(collapseShared))]
}
